/**
 * Per-customer Appointment History drawer.
 *
 * Triggered from the Müşteriler page → "Geçmiş" action. Pulls
 * `/api/users/{id}/appointment-history` and renders aggregated stats +
 * the chronological list of past appointments.
 */
import { useEffect, useState } from 'react';
import axios from 'axios';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from '@/components/ui/dialog';
import { Card, CardContent } from '@/components/ui/card';
import { History, CalendarDays, Phone, Mail, TrendingUp, UserX, X as XIcon } from 'lucide-react';
import { useSectorTerms } from '@/utils/useSectorTerms';

const STATUS_LABEL = {
  scheduled:        { text: 'Onaylandı',    color: 'bg-emerald-100 text-emerald-800 border-emerald-200' },
  pending_approval: { text: 'Onay Bekliyor', color: 'bg-blue-100 text-blue-800 border-blue-200' },
  completed:        { text: 'Tamamlandı',   color: 'bg-gray-100 text-gray-700 border-gray-200' },
  cancelled:        { text: 'İptal Edildi', color: 'bg-red-100 text-red-700 border-red-200' },
  rejected:         { text: 'Reddedildi',   color: 'bg-red-100 text-red-700 border-red-200' },
  no_show:          { text: 'Gelmedi',      color: 'bg-amber-100 text-amber-800 border-amber-200' },
};

const fmtDate = (iso) => {
  if (!iso) return '—';
  try {
    return new Date(iso + 'T00:00:00').toLocaleDateString('tr-TR', {
      day: '2-digit', month: '2-digit', year: 'numeric',
    });
  } catch { return iso; }
};

export const AppointmentHistoryDrawer = ({ userId, open, onClose }) => {
  const API_URL = process.env.REACT_APP_API_URL;
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const terms = useSectorTerms();

  useEffect(() => {
    if (!open || !userId) { setData(null); return; }
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const { data: res } = await axios.get(
          `${API_URL}/api/users/${userId}/appointment-history`,
          { withCredentials: true },
        );
        if (!cancelled) setData(res);
      } catch {
        if (!cancelled) setData(null);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [open, userId, API_URL]);

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose?.()}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="appointment-history-drawer">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><History className="w-5 h-5 text-[#2563EB]" /> Randevu Geçmişi</DialogTitle>
          <DialogDescription asChild>
            {data?.customer ? (
              <div className="flex flex-wrap gap-3 text-sm mt-1">
                <span className="font-medium text-[#0F172A]">{data.customer.name}</span>
                {data.customer.phone && <span className="text-[#595959] inline-flex items-center gap-1"><Phone className="w-3.5 h-3.5" />{data.customer.phone}</span>}
                {data.customer.email && !data.customer.email.includes('no-email.paydaai.local') && (
                  <span className="text-[#595959] inline-flex items-center gap-1"><Mail className="w-3.5 h-3.5" />{data.customer.email}</span>
                )}
              </div>
            ) : <span>{terms.customerLabelSingular} detayı</span>}
          </DialogDescription>
        </DialogHeader>

        {loading && <p className="text-center py-8 text-[#999]">Yükleniyor...</p>}

        {!loading && data && (
          <div className="space-y-4">
            {/* Stats */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
              <Card className="border-[#E5E5E5]"><CardContent className="p-3 text-center">
                <p className="text-2xl font-bold text-[#0F172A]" data-testid="history-stat-total">{data.stats.total}</p>
                <p className="text-[10px] text-[#595959] uppercase tracking-wide mt-1">Toplam</p>
              </CardContent></Card>
              <Card className="border-emerald-200 bg-emerald-50"><CardContent className="p-3 text-center">
                <p className="text-2xl font-bold text-emerald-700" data-testid="history-stat-completed">{data.stats.completed}</p>
                <p className="text-[10px] text-emerald-700 uppercase tracking-wide mt-1">Tamamlanan</p>
              </CardContent></Card>
              <Card className="border-amber-200 bg-amber-50"><CardContent className="p-3 text-center">
                <p className="text-2xl font-bold text-amber-700" data-testid="history-stat-noshow">{data.stats.no_show}</p>
                <p className="text-[10px] text-amber-700 uppercase tracking-wide mt-1">Gelmedi</p>
              </CardContent></Card>
              <Card className="border-red-200 bg-red-50"><CardContent className="p-3 text-center">
                <p className="text-2xl font-bold text-red-700" data-testid="history-stat-cancel">{data.stats.cancelled}</p>
                <p className="text-[10px] text-red-700 uppercase tracking-wide mt-1">İptal</p>
              </CardContent></Card>
            </div>

            {/* Secondary stats */}
            <div className="flex flex-wrap items-center justify-between gap-3 px-1 py-2 bg-[#F9FAFB] rounded-md text-sm">
              <span className="flex items-center gap-1.5 text-[#595959]">
                <CalendarDays className="w-4 h-4" /> Son ziyaret:&nbsp;
                <strong className="text-[#0F172A]" data-testid="history-last-visit">{fmtDate(data.stats.last_visit)}</strong>
              </span>
              <span className="flex items-center gap-1.5 text-[#595959]">
                <TrendingUp className="w-4 h-4 text-emerald-600" /> Toplam ciro:&nbsp;
                <strong className="text-emerald-700" data-testid="history-revenue">{Number(data.stats.revenue || 0).toLocaleString('tr-TR')} ₺</strong>
              </span>
            </div>

            {/* List */}
            <div className="space-y-2" data-testid="history-list">
              {data.appointments.length === 0 ? (
                <div className="text-center py-8 text-[#999]">
                  <CalendarDays className="w-10 h-10 mx-auto mb-2 opacity-40" />
                  <p>Bu müşteriye ait randevu kaydı yok</p>
                </div>
              ) : data.appointments.map((apt) => {
                const s = STATUS_LABEL[apt.status] || STATUS_LABEL.scheduled;
                return (
                  <div key={apt.id} className="border border-[#E5E5E5] rounded-lg p-3 hover:bg-[#FAFAFA]" data-testid={`history-row-${apt.id}`}>
                    <div className="flex items-center justify-between gap-2 mb-1">
                      <div className="font-medium text-[#0F172A]">{apt.service}</div>
                      <span className={`px-2 py-0.5 text-[10px] border rounded-full ${s.color}`}>{s.text}</span>
                    </div>
                    <div className="text-xs text-[#595959] flex flex-wrap gap-x-3 gap-y-1">
                      <span>{fmtDate(apt.date)} · {apt.start_time}-{apt.end_time}</span>
                      {apt.staff_name && <span>· {apt.staff_name}</span>}
                      {apt.price ? <span>· {Number(apt.price).toLocaleString('tr-TR')} ₺</span> : null}
                    </div>
                    {apt.notes && <p className="text-xs text-[#595959] italic mt-1.5 pt-1.5 border-t border-[#F3F4F6]">"{apt.notes}"</p>}
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {!loading && !data && (
          <p className="text-center py-8 text-red-500 text-sm">Veriler alınamadı.</p>
        )}
      </DialogContent>
    </Dialog>
  );
};
